import { ApplicationRef, Injectable } from '@angular/core';
import { SchoolCoursesApiService, SchoolCoursesService } from '@digilize/language-school/data';
import {
  ChapterMarker,
  CourseChapter,
  LessonMarker,
  MapType,
  SchoolLesson,
  SchoolLessonWithExercises,
} from '@digilize/language-school/definitions';
import { Media } from '@digilize/shared/definitions/src';
import { SnackbarService } from '@digilize/shared/feature/modules/snackbar/src';
import { ImageOverlay } from '@lsc/src/app/views/map/ImageOverlay';
import { ZOOMS } from '@lsc/src/app/views/map/zooms';
import { BehaviorSubject, combineLatest, filter, first, map, switchMap } from 'rxjs';

/**
 * @param  mapInitialized - set to true when map object is ready to be used
 * @param  mapAssetsLoaded - information about elements loaded on top of map (overlays, markers and other assets)
 * @param  mapMinimized - map is always in dom, if certain view is not using map it should be true, so will be hidden behind other components
 * @param  mapJustDragEnded - used to patch cases where draggingEnd was firing marker click logic
 */
@Injectable({
  providedIn: 'root',
})
export class MapService {
  private _mapAssetsLoaded = new BehaviorSubject(false);
  mapAssetsLoaded$ = this._mapAssetsLoaded.asObservable();

  _mapInitialized = new BehaviorSubject(false);
  mapInitialized$ = this._mapInitialized.asObservable();
  get mapInitialized() {
    return this._mapInitialized.value;
  }
  set mapInitialized(mapInitialized: boolean) {
    this._mapInitialized.next(mapInitialized);
  }
  private _mapMinimized = new BehaviorSubject(true);
  mapMinimized$ = this._mapMinimized.asObservable();
  set mapMinimized(mapMinimized: boolean) {
    this._mapMinimized.next(mapMinimized);
  }

  private _mapJustDragEnded = new BehaviorSubject(false);
  mapJustDragEnded$ = this._mapJustDragEnded.asObservable();
  get mapJustDragEnded() {
    return this._mapJustDragEnded.value;
  }
  set mapJustDragEnded(mapJustDragEnded: boolean) {
    this._mapJustDragEnded.next(mapJustDragEnded);
  }

  map: google.maps.Map;
  polylineAnimatedParts: google.maps.Polyline[] = [];
  toRemoveOnDestroy: { setMap: any }[] = [];
  private _currentMap = new BehaviorSubject<CurrentMap>(null);
  currentMap$ = this._currentMap.asObservable().pipe(filter((map) => !!map));
  get currentMap() {
    return this._currentMap.value;
  }
  set currentMap(currentMap: CurrentMap) {
    this._currentMap.next(currentMap);
    this.setMap(currentMap);
  }
  constructor(
    private schoolCoursesService: SchoolCoursesService,
    private schoolCoursesApiService: SchoolCoursesApiService,
    private applicationRef: ApplicationRef,
    private snackbarService: SnackbarService
  ) {
    this.applicationRef.detachView;
    combineLatest([this.schoolCoursesService.courses$, this.schoolCoursesService.currentCourseId$])
      .pipe(
        first(([courses, currentCourseId]) => currentCourseId !== 'noCourse'),
        switchMap(([courses, currentCourseId]) => {
          return this.schoolCoursesApiService.getCourseMap(currentCourseId).pipe(
            map((courseMap) => ({
              course: courses.courses.find((course) => course.id === currentCourseId),
              courseMap,
              currentCourseId,
            }))
          );
        })
      )
      .subscribe(({ course, courseMap, currentCourseId }) => {
        const map_elements: (SchoolLessonWithExercises | CourseChapter)[] = course.chapters;
        this.currentMap = { ...course, map_elements, ...courseMap, map_type: MapType.Province };
      });
  }

  setMapAssetsLoaded() {
    this._mapAssetsLoaded.next(true);
  }

  setMap(currentMap: CurrentMap) {
    if (this.mapInitialized || this.map) {
      this.clearElements();
      this.updateMapOptions(currentMap);
      return;
    }
    const { zoom_default, zoom_max, zoom_min } = this.currentMap;
    this.map = new google.maps.Map(document.getElementById('map'), {
      center: this.getCenter(currentMap),
      zoom: zoom_default,
      maxZoom: zoom_max,
      minZoom: zoom_min,
      scrollwheel: true,
      disableDefaultUI: true,
      disableDoubleClickZoom: true,
      keyboardShortcuts: false,
      clickableIcons: false,
      zoomControl: true,
      noClear: true,
      rotateControl: true,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      mapId: '19847e3eab9d3223',
      restriction: {
        latLngBounds: this.getBounds(currentMap),
        strictBounds: true,
      },
      gestureHandling: 'greedy',
    } as google.maps.MapOptions & { mapId: string });

    this.map.addListener('mapcapabilities_changed', () => {
      const mapCapabilities = (this.map as any).getMapCapabilities();
      if (!mapCapabilities.isAdvancedMarkersAvailable) {
        console.error('advanced markers not available');
      }
      if (!this.mapInitialized) {
        this.mapInitialized = true;
      }
    });

    this.map.addListener('dragend', () => {
      this.mapJustDragEnded = true;
      setTimeout(() => {
        this.mapJustDragEnded = false;
      }, 0);
    });
  }

  updateMapOptions(currentMap: CurrentMap) {
    const { zoom_max, zoom_min, zoom_default } = currentMap;
    /* min max has to be changed in two steps, otherwise errors like minZoom exceeds maxZoom will appear */
    this.map.setOptions({
      restriction: { strictBounds: true, latLngBounds: this.getBounds(currentMap) },
      center: this.getCenter(this.currentMap),
      minZoom: null,
      maxZoom: null,
    });
    this.map.setOptions({
      zoom: zoom_default,
      minZoom: zoom_min >= zoom_max ? zoom_max : zoom_min,
      maxZoom: zoom_min < zoom_max ? zoom_max : zoom_min,
    });
  }
  private getCenter(currentMap: CurrentMap): { lat: number; lng: number } {
    const { boundary_bottom, boundary_left, boundary_right, boundary_top } = currentMap;
    if (isNaN((boundary_top + boundary_bottom) / 2)) {
      const { boundary_bottom, boundary_left, boundary_right, boundary_top } = {
        boundary_top: 54.9949,
        boundary_bottom: 41.6259,
        boundary_right: 9.5667,
        boundary_left: -2.1714,
      };
      return { lat: (boundary_top + boundary_bottom) / 2, lng: (boundary_left + boundary_right) / 2 };
    }

    return { lat: (boundary_top + boundary_bottom) / 2, lng: (boundary_left + boundary_right) / 2 };
  }
  private getBounds(currentMap: CurrentMap): google.maps.LatLngBoundsLiteral {
    const { boundary_bottom, boundary_left, boundary_right, boundary_top } = currentMap;
    let cords = { lat: (boundary_top + boundary_bottom) / 2, lng: (boundary_left + boundary_right) / 2 };
    if (isNaN(cords.lat)) {
      console.warn('cant init bounds');
      return {
        north: 54.9949,
        south: 41.6259,
        east: 9.5667,
        west: -2.1714,
      };
    }
    return {
      east: boundary_right,
      south: boundary_bottom,
      west: boundary_left,
      north: boundary_top,
    };
  }

  private clearElements() {
    [...this.toRemoveOnDestroy, ...this.polylineAnimatedParts].forEach((part) => {
      (part as any).setMap(null);
    });
  }

  drawImageOverlayFromSrc(url?: string | null, imageBounds?: google.maps.LatLngBounds) {
    if (!url || !imageBounds) {
      this.setMapAssetsLoaded();
      return;
    }

    const overlay: ImageOverlay = new ImageOverlay(imageBounds, url, 'fromSrc');

    overlay.setMap(this.map);

    overlay.addListener('imgLoaded', () => {
      this.setMapAssetsLoaded();
    });
    this.toRemoveOnDestroy.push(overlay);
  }

  drawImageOverlayFromSvg(svg?: string | null, imageBounds?: google.maps.LatLngBounds) {
    if (!svg || !imageBounds) {
      this.setMapAssetsLoaded();
      return;
    }
    const container = document.createElement('div');
    container.innerHTML = svg;
    const svgElement = container.querySelector('svg');

    /*  const overlay: CanvasImageOverlay = new CanvasImageOverlay(imageBounds, svgElement, this.map); */
    const overlay: ImageOverlay = new ImageOverlay(imageBounds, svgElement, 'fromSvg');

    overlay.setMap(this.map);

    this.toRemoveOnDestroy.push(overlay);
    this.setMapAssetsLoaded();
  }

  centerToTooltip(lat: number, lng: number, element: HTMLElement) {
    this.map.panTo({ lat, lng });
    const buildingHeight = (element.querySelector('.building-container') as HTMLElement).getBoundingClientRect().height;
    const OFFSET = 180 - buildingHeight / 3;

    setTimeout(() => {
      const tooltip = element.querySelector('.tooltip') as HTMLElement;

      const isTooltipImgLoaded = tooltip.querySelector('img').height !== 0;
      let tooltipHeight = tooltip.offsetHeight;
      if (!isTooltipImgLoaded) {
        const APPROXIMATE_IMAGE_HEIGHT_WITH_GAP = 185;
        tooltipHeight += APPROXIMATE_IMAGE_HEIGHT_WITH_GAP;
      }
      const by = -tooltipHeight + OFFSET;
      this.map.panBy(0, by);
    }, 30); /* some time for img to load, its better to use real image than approximate height */
  }

  public setTooltipPosition(
    marker: ChapterMarker | LessonMarker,
    markerContainer: HTMLElement,
    extraScaling: number = 1
  ) {
    if (!marker.display) return;
    marker.hidden = false;
    const projection = this.map.getProjection();
    if (!projection) return; // Projection is available after map is initialized
    const buildingPosition = new google.maps.LatLng(marker.lat, marker.lon);
    const scale = Math.pow(2, this.map.getZoom());

    const worldPoint = projection.fromLatLngToPoint(buildingPosition);
    const buildingPixel = new google.maps.Point(worldPoint.x * scale, worldPoint.y * scale);

    const topLeftLatLng = new google.maps.LatLng(this.currentMap.boundary_top, this.currentMap.boundary_left);

    const topLeftWorldPoint = projection.fromLatLngToPoint(topLeftLatLng);
    const topLeftPixel = new google.maps.Point(topLeftWorldPoint.x * scale, topLeftWorldPoint.y * scale);
    const verticalDistance = buildingPixel.y - topLeftPixel.y;
    const tooltipWrapper = markerContainer.querySelector('.map-tooltip-wrapper') as HTMLElement;

    tooltipWrapper.classList.remove('below');
    tooltipWrapper.classList.remove('above');
    const building = (markerContainer.querySelector('.building-container') as HTMLElement).getBoundingClientRect();
    const buildingHeight = building.height;
    const buildingTop = building.top;

    const heightOfWrapperWithOffset = tooltipWrapper.getBoundingClientRect().height + 60;

    if (heightOfWrapperWithOffset > verticalDistance) {
      const scale =
        1 /
        ZOOMS[this.map.getZoom()].buildingScale /
        Number.parseFloat(markerContainer.getAttribute('data-scale')) /
        extraScaling;
      const canBePlacedBelowBuilding =
        buildingTop + heightOfWrapperWithOffset + buildingHeight + 60 < window.innerHeight;
      if (canBePlacedBelowBuilding) {
        tooltipWrapper.classList.add('below');
        tooltipWrapper.style.top = `${(heightOfWrapperWithOffset + buildingHeight - 40) * scale}px`;
      } else {
        tooltipWrapper.style.top = `${(heightOfWrapperWithOffset + buildingHeight - verticalDistance) * scale}px`;
      }
    } else {
      tooltipWrapper.classList.add('above');
    }
  }
}

export interface CurrentMap {
  boundary_bottom: number;
  boundary_left: number;
  boundary_right: number;
  boundary_top: number;
  map_type: MapType;
  zoom_min: number;
  zoom_default: number;
  zoom_max: number;
  map_elements: (CourseChapter | SchoolLesson)[];
  overlay?: string | Media;
  display_name: string;
}
